/**
 * Filter class to handle log filtering logic
 */
export class Filter {
  constructor({ text = '', textFilters = [], containers = [], invertText = false } = {}) {
    this.text = text;
    this.textFilters = textFilters; // Array of { text, invert } objects
    this.containers = containers;
    this.invertText = invertText;
  }

  /**
   * Create a new filter by combining this filter with another
   */
  combine(otherFilter) {
    // Build combined text filters array with individual invert states
    const allTextFilters = [...this.textFilters];
    
    // Add this filter's text if it exists
    if (this.text) {
      allTextFilters.push({ text: this.text, invert: this.invertText });
    }
    
    // Add other filter's existing text filters
    allTextFilters.push(...otherFilter.textFilters);
    
    // Add other filter's text if it exists
    if (otherFilter.text) {
      allTextFilters.push({ text: otherFilter.text, invert: otherFilter.invertText });
    }

    // Intersect container filters (only containers that pass both filters)
    const thisContainers = new Set(this.containers);
    const otherContainers = new Set(otherFilter.containers);
    const intersectedContainers = thisContainers.size > 0
      ? [...otherContainers].filter(c => thisContainers.has(c))
      : [...otherContainers];

    return new Filter({
      text: '',
      textFilters: allTextFilters,
      containers: intersectedContainers,
      invertText: false
    });
  }

  /**
   * Apply this filter to a log entry
   * @param {Object} log - Log entry with { message, container, ... }
   * @returns {boolean} - Whether the log passes the filter
   */
  apply(log) {
    const logMessage = log.message.toLowerCase();
    const allowedContainers = new Set(this.containers);

    // Check container filter
    if (!allowedContainers.has(log.container) && log.container !== 'system') {
      return false;
    }

    // Apply all text filters from textFilters array (AND logic)
    // Each filter has its own invert state
    if (this.textFilters.length > 0) {
      for (const filter of this.textFilters) {
        const filterText = filter.text.toLowerCase();
        const matches = logMessage.includes(filterText);
        if (filter.invert ? matches : !matches) {
          return false;
        }
      }
    }

    // Apply current text filter (for backwards compatibility and active filtering)
    if (this.text) {
      const filterText = this.text.toLowerCase();
      const matches = logMessage.includes(filterText);
      if (this.invertText ? matches : !matches) {
        return false;
      }
    }

    return true;
  }

  /**
   * Convert to plain object for state storage
   */
  toObject() {
    return {
      text: this.text,
      textFilters: this.textFilters,
      containers: this.containers,
      invertText: this.invertText
    };
  }

  /**
   * Create Filter from plain object
   */
  static fromObject(obj) {
    return new Filter(obj);
  }

  /**
   * Create an empty filter with all containers
   */
  static empty(containers = []) {
    return new Filter({
      text: '',
      textFilters: [],
      containers: [...containers],
      invertText: false
    });
  }
}

/**
 * Apply multiple filters to a collection of logs
 * @param {Array} logs - Array of log entries
 * @param {Filter} baseFilter - Base filter to apply first
 * @param {Filter} currentFilter - Current/active filter to apply second
 * @returns {Array} - Filtered logs
 */
export function applyFilters(logs, baseFilter, currentFilter) {
  return logs.filter(log => {
    // Apply base filter first
    if (baseFilter && !baseFilter.apply(log)) {
      return false;
    }
    
    // Apply current filter
    if (currentFilter && !currentFilter.apply(log)) {
      return false;
    }
    
    return true;
  });
}

